/**
 * Records manager — persists game leaderboard to localStorage.
 *
 * Stores the top 10 game records sorted by penalized time (ascending).
 * Follows the same pattern as statisticsManager.ts and settingsManager.ts.
 */

import { GameRecord, UNDO_PENALTY_MS, MAX_RECORDS } from '../Models/GameRecord'

const RECORDS_KEY = 'solitaire-records'

// ─── Persistence ────────────────────────────────────────────────────────────

/** Load all records from localStorage. Returns empty array if none exist or data is corrupt. */
export function loadRecords(): GameRecord[] {
    try {
        const stored = localStorage.getItem(RECORDS_KEY)
        if (stored) {
            const parsed = JSON.parse(stored)
            if (!Array.isArray(parsed)) return []
            // Filter out any entries that don't have the required numeric fields
            const valid = parsed.filter(
                (r: unknown) =>
                    typeof r === 'object' &&
                    r !== null &&
                    typeof (r as GameRecord).rawTime === 'number' &&
                    typeof (r as GameRecord).penalizedTime === 'number' &&
                    typeof (r as GameRecord).undoCount === 'number'
            ) as GameRecord[]
            // Ensure sorted by penalized time (ascending) — defensive
            return valid.sort((a, b) => a.penalizedTime - b.penalizedTime).slice(0, MAX_RECORDS)
        }
    } catch (error) {
        console.warn('Failed to load records, using empty list:', error)
    }
    return []
}

/** Save records to localStorage. */
function saveRecords(records: GameRecord[]): void {
    try {
        localStorage.setItem(RECORDS_KEY, JSON.stringify(records))
    } catch (error) {
        console.error('Failed to save records:', error)
    }
}

/**
 * Save a new game record to the leaderboard.
 *
 * @param rawTime — Time to complete the game in milliseconds (no penalty)
 * @param undoCount — Number of intentional undo button presses
 * @returns The record's rank (1-based), or null if it didn't make the top 10
 */
export function saveRecord(rawTime: number, undoCount: number): number | null {
    const penalizedTime = rawTime + undoCount * UNDO_PENALTY_MS
    const record: GameRecord = {
        rawTime,
        undoCount,
        penalizedTime,
        date: new Date().toISOString(),
    }

    const records = loadRecords()

    // Find insertion index (sorted ascending by penalized time)
    let insertIndex = records.length
    for (let i = 0; i < records.length; i++) {
        if (penalizedTime < records[i].penalizedTime) {
            insertIndex = i
            break
        }
    }

    // If we're full and this record doesn't beat any existing one, skip it
    if (insertIndex >= MAX_RECORDS) {
        return null
    }

    // Insert and trim to max size
    records.splice(insertIndex, 0, record)
    const trimmed = records.slice(0, MAX_RECORDS)
    saveRecords(trimmed)

    // Return 1-based rank
    return insertIndex + 1
}
